import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowUpRight, ArrowRight,
  Newspaper, Brain, Robot, BookBookmark, Buildings, Rocket,
  Money, CalendarBlank, GraduationCap, Code, Wrench, ShoppingBag,
  CheckCircle, Star, Fire, TrendUp, Clock, Users, MapPin, Tag,
  CurrencyDollar, Globe, Certificate, ChartBar, Sparkle,
  ArrowSquareOut, BookOpen, LightbulbFilament, Pulse,
} from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import AINavBar from "@/components/AINavBar";
import { fetchAIItem, fetchAISection } from "@/lib/api";
import { stripHtml } from "@/lib/stripHtml";

function fmt(val) {
  if (!val && val !== 0) return "—";
  return String(val);
}

// ── Section metadata ──────────────────────────────────────────────────────────

const SECTION_META = {
  news:        { label: "AI News",        Icon: Newspaper,    color: "bg-sky-50 text-sky-700 border-sky-200",          accent: "text-sky-600",     refreshLabel: "every 4 hours" },
  models:      { label: "AI Models",      Icon: Brain,        color: "bg-violet-50 text-violet-700 border-violet-200", accent: "text-violet-600",  refreshLabel: "daily" },
  agents:      { label: "AI Agents",      Icon: Robot,        color: "bg-blue-50 text-blue-700 border-blue-200",       accent: "text-blue-600",    refreshLabel: "daily" },
  research:    { label: "AI Research",    Icon: BookBookmark, color: "bg-amber-50 text-amber-700 border-amber-200",    accent: "text-amber-600",   refreshLabel: "daily" },
  companies:   { label: "AI Companies",   Icon: Buildings,    color: "bg-emerald-50 text-emerald-700 border-emerald-200", accent: "text-emerald-600", refreshLabel: "daily" },
  startups:    { label: "AI Startups",    Icon: Rocket,       color: "bg-orange-50 text-orange-700 border-orange-200", accent: "text-orange-600",  refreshLabel: "daily" },
  funding:     { label: "AI Funding",     Icon: Money,        color: "bg-green-50 text-green-700 border-green-200",    accent: "text-green-600",   refreshLabel: "daily" },
  events:      { label: "AI Events",      Icon: CalendarBlank,color: "bg-pink-50 text-pink-700 border-pink-200",       accent: "text-pink-600",    refreshLabel: "daily" },
  learning:    { label: "AI Learning",    Icon: GraduationCap,color: "bg-yellow-50 text-yellow-700 border-yellow-200", accent: "text-yellow-600",  refreshLabel: "daily" },
  coding:      { label: "AI Coding",      Icon: Code,         color: "bg-cyan-50 text-cyan-700 border-cyan-200",       accent: "text-cyan-600",    refreshLabel: "daily" },
  tools:       { label: "AI Tools",       Icon: Wrench,       color: "bg-purple-50 text-purple-700 border-purple-200", accent: "text-purple-600",  refreshLabel: "daily" },
  marketplace: { label: "AI Marketplace", Icon: ShoppingBag,  color: "bg-teal-50 text-teal-700 border-teal-200",      accent: "text-teal-600",    refreshLabel: "daily" },
};

const SECTION_DESCRIPTIONS = {
  news:        "TrendMing aggregates breaking AI developments from 50+ sources, curated for signal over noise.",
  models:      "Independent capability and benchmark tracking across all frontier and open-source AI models.",
  research:    "Peer-reviewed AI papers from arXiv and top venues, surfaced by relevance and citation momentum.",
  companies:   "Company intelligence on the organizations shaping the future of AI — from infrastructure to applications.",
  startups:    "Early signals on high-growth AI ventures before they reach mainstream coverage.",
  funding:     "Capital flow intelligence — tracking where investors are placing bets in the AI landscape.",
  agents:      "Coverage of autonomous AI systems — from coding agents to web browsers to enterprise orchestrators.",
  events:      "The AI conference and summit calendar, curated for practitioners and researchers.",
  learning:    "Vetted learning resources for building AI skills — from beginner foundations to advanced research.",
  coding:      "Developer tools and AI coding assistants, ranked by adoption and real-world utility.",
  tools:       "The comprehensive AI tool landscape across all domains and use cases.",
  marketplace: "APIs, services, and infrastructure products powering AI applications in production.",
};

const EXPLORE_LINKS = [
  { section: "news",      label: "AI News",        Icon: Newspaper    },
  { section: "models",    label: "AI Models",      Icon: Brain        },
  { section: "agents",    label: "AI Agents",      Icon: Robot        },
  { section: "research",  label: "AI Research",    Icon: BookBookmark },
  { section: "companies", label: "AI Companies",   Icon: Buildings    },
  { section: "startups",  label: "AI Startups",    Icon: Rocket       },
  { section: "funding",   label: "AI Funding",     Icon: Money        },
  { section: "events",    label: "AI Events",      Icon: CalendarBlank },
  { section: "learning",  label: "AI Learning",    Icon: GraduationCap },
  { section: "coding",    label: "AI Coding",      Icon: Code         },
  { section: "tools",     label: "AI Tools",       Icon: Wrench       },
  { section: "marketplace", label: "AI Marketplace", Icon: ShoppingBag },
];

// ── Shared UI atoms ───────────────────────────────────────────────────────────

function Badge({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${className}`}>
      {children}
    </span>
  );
}

function InfoRow({ label, value, icon: Icon }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-center justify-between px-5 py-3">
      <span className="text-xs text-slate-400 font-medium flex items-center gap-1.5">
        {Icon && <Icon size={11} className="text-slate-300" />}
        {label}
      </span>
      <span className="text-xs font-semibold text-slate-700 text-right max-w-[60%]">{value}</span>
    </div>
  );
}

function SectionCard({ title, children, icon: Icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
        {Icon && <Icon size={14} className="text-slate-400" />}
        <h2 className="text-base font-bold text-slate-900">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function TagChip({ tag }) {
  return (
    <span className="text-[11px] font-medium px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">
      {tag}
    </span>
  );
}

function BenchmarkBar({ label, value }) {
  if (!value && value !== 0) return null;
  const pct = Math.min(100, Math.max(0, value));
  const color = pct >= 80 ? "bg-emerald-500" : pct >= 60 ? "bg-amber-400" : "bg-red-400";
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-slate-500">{label}</span>
        <span className="text-xs font-bold text-slate-700">{value}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function AttributionFooter({ item, section }) {
  const sourceLabel = item.publisher || item.provider || item.source || null;
  const sourceUrl = item.url || item.website || null;
  return (
    <div className="mt-6 pt-4 border-t border-slate-100 flex items-start gap-2 text-[11px] text-slate-400 leading-relaxed">
      <Globe size={11} className="mt-0.5 flex-shrink-0" />
      <span>
        Data aggregated and editorially reviewed by TrendMing.{" "}
        {sourceLabel && sourceUrl && (
          <>
            Original source:{" "}
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-slate-600 transition-colors"
            >
              {sourceLabel}
            </a>
            .
          </>
        )}
      </span>
    </div>
  );
}

function TrendMingIntelligenceCard({ section, meta }) {
  const desc = SECTION_DESCRIPTIONS[section] || "Curated AI intelligence, updated regularly by TrendMing.";
  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Sparkle size={13} className="text-blue-600" weight="fill" />
        <span className="text-[11px] font-bold text-blue-800 uppercase tracking-wider">TrendMing Intelligence</span>
      </div>
      <p className="text-xs text-blue-700 leading-relaxed">{desc}</p>
      <div className="mt-3 flex items-center gap-1.5 text-[11px] text-blue-500">
        <Pulse size={10} weight="fill" />
        <span>Updated {meta.refreshLabel || "daily"}</span>
      </div>
    </div>
  );
}

function InsightsBullets({ bullets, accentClass = "text-blue-600" }) {
  if (!bullets || bullets.length === 0) return null;
  return (
    <ul className="space-y-2.5">
      {bullets.map((b, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <LightbulbFilament size={14} className={`${accentClass} flex-shrink-0 mt-0.5`} weight="fill" />
          <span className="text-sm text-slate-700 leading-relaxed">{b}</span>
        </li>
      ))}
    </ul>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function PageSkeleton() {
  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 animate-pulse">
      <div className="h-4 bg-slate-200 rounded w-48 mb-8" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-8">
            <div className="h-6 bg-slate-200 rounded w-1/4 mb-4" />
            <div className="h-8 bg-slate-200 rounded w-3/4 mb-3" />
            <div className="h-4 bg-slate-200 rounded w-1/2 mb-6" />
            <div className="space-y-2">
              <div className="h-3 bg-slate-200 rounded w-full" />
              <div className="h-3 bg-slate-200 rounded w-5/6" />
              <div className="h-3 bg-slate-200 rounded w-4/6" />
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-8">
            <div className="h-5 bg-slate-200 rounded w-1/3 mb-5" />
            <div className="space-y-3">
              {[1, 2, 3].map((i) => <div key={i} className="h-3 bg-slate-200 rounded" />)}
            </div>
          </div>
        </div>
        <div className="space-y-5">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="h-4 bg-slate-200 rounded w-1/2 mb-4" />
            {[1, 2, 3, 4].map((i) => <div key={i} className="h-3 bg-slate-200 rounded mb-3" />)}
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="h-4 bg-slate-200 rounded w-1/3 mb-4" />
            {[1, 2, 3].map((i) => <div key={i} className="h-12 bg-slate-200 rounded-lg mb-2" />)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Section-specific content components ──────────────────────────────────────

function NewsContent({ item }) {
  const date = item.publishedAt || item.published_at || item.date || "Recently published";
  const excerpt = stripHtml(item.excerpt || item.summary || "");
  // Show a 2-sentence teaser only to avoid reproducing full publisher content
  const teaser = excerpt.split(". ").slice(0, 2).join(". ").trim();
  const teaserDisplay = teaser && !teaser.endsWith(".") ? teaser + "." : teaser;

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden p-6 md:p-8">
        {item.thumbnail ? (
          <div className="-mx-6 -mt-6 md:-mx-8 md:-mt-8 mb-6 aspect-video overflow-hidden bg-slate-100">
            <img
              src={item.thumbnail}
              alt={stripHtml(item.title)}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.parentElement.style.display = "none"; }}
            />
          </div>
        ) : (
          <div className="-mx-6 -mt-6 md:-mx-8 md:-mt-8 mb-6 h-[200px] bg-gradient-to-br from-slate-800 to-blue-950" />
        )}
        <div className="flex flex-wrap gap-2 mb-4">
          {item.category && <Badge className="bg-sky-50 text-sky-700 border-sky-200">{item.category}</Badge>}
          {(item.hot || item.isHot) && <Badge className="bg-red-50 text-red-600 border-red-200"><Fire size={9} weight="fill" /> HOT</Badge>}
          {item.trending && <Badge className="bg-blue-50 text-blue-600 border-blue-200"><TrendUp size={9} /> TRENDING</Badge>}
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 leading-snug mb-4">
          {stripHtml(item.title)}
        </h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-5">
          {item.author && <span>by {item.author}</span>}
          {date && <span className="flex items-center gap-1"><Clock size={13} /> {date}</span>}
          {item.readTime && <span>{item.readTime} read</span>}
        </div>
        {teaserDisplay && (
          <p className="text-slate-700 leading-relaxed text-base mb-5">{teaserDisplay}</p>
        )}
        {item.url && (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold text-sky-600 hover:text-sky-700 transition-colors border border-sky-200 bg-sky-50 px-4 py-2 rounded-lg"
          >
            Continue reading at source <ArrowSquareOut size={13} />
          </a>
        )}
        <AttributionFooter item={item} section="news" />
      </div>

      {/* TrendMing Perspective */}
      {item.tags && item.tags.length > 0 && (
        <SectionCard title="Topics Covered" icon={Tag}>
          <div className="flex flex-wrap gap-2">
            {item.tags.map((t) => <TagChip key={t} tag={t} />)}
          </div>
        </SectionCard>
      )}
    </>
  );
}

function ModelsContent({ item }) {
  const benchmarks = item.benchmarks || {};
  const pricing = item.pricing || {};
  const caps = item.capabilities || [];

  const capColor = (c) => {
    const map = {
      Text: "bg-blue-50 text-blue-700 border-blue-200",
      Code: "bg-emerald-50 text-emerald-700 border-emerald-200",
      Vision: "bg-purple-50 text-purple-700 border-purple-200",
      Audio: "bg-amber-50 text-amber-700 border-amber-200",
      Video: "bg-rose-50 text-rose-700 border-rose-200",
      Reasoning: "bg-indigo-50 text-indigo-700 border-indigo-200",
      Agents: "bg-cyan-50 text-cyan-700 border-cyan-200",
      Math: "bg-orange-50 text-orange-700 border-orange-200",
    };
    return map[c] || "bg-slate-100 text-slate-600 border-slate-200";
  };

  // Synthesize a performance summary sentence from benchmark scores
  const bScores = Object.entries(benchmarks).filter(([, v]) => v > 0);
  const topBench = bScores.sort(([, a], [, b]) => b - a)[0];
  const perfSummary = topBench
    ? `Scores ${topBench[1]}% on ${topBench[0]}${bScores.length > 1 ? `, competitive across ${bScores.length} standard benchmarks` : ""}.`
    : null;

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{stripHtml(item.name)}</h1>
              {(item.isNew || item.is_new) && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">NEW</Badge>}
              {(item.hot || item.isHot) && <Badge className="bg-red-50 text-red-600 border-red-200"><Fire size={9} weight="fill" /> HOT</Badge>}
            </div>
            <p className="text-sm text-slate-500">{stripHtml(item.provider)} · {item.family || item.model_family || ""}</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {item.openSource || item.open_source ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Open Source</Badge> : null}
            {item.apiAvailable || item.api_available ? <Badge className="bg-blue-50 text-blue-700 border-blue-200">API</Badge> : null}
          </div>
        </div>
        <p className="text-slate-700 leading-relaxed mb-5">{stripHtml(item.description)}</p>
        {caps.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {caps.map((c) => <Badge key={c} className={capColor(c)}>{c}</Badge>)}
          </div>
        )}
        {perfSummary && (
          <p className="text-sm text-slate-500 italic border-t border-slate-100 pt-4 mt-4">{perfSummary}</p>
        )}
        <AttributionFooter item={item} section="models" />
      </div>

      {bScores.length > 0 && (
        <SectionCard title="Benchmark Performance" icon={ChartBar}>
          <div className="space-y-4">
            <BenchmarkBar label="MMLU" value={benchmarks.MMLU} />
            <BenchmarkBar label="HumanEval" value={benchmarks.HumanEval} />
            <BenchmarkBar label="MATH" value={benchmarks.MATH} />
            <BenchmarkBar label="GPQA" value={benchmarks.GPQA} />
          </div>
          <p className="text-[11px] text-slate-400 mt-4">Scores sourced from published evaluations. Higher = better.</p>
        </SectionCard>
      )}

      {(pricing.input || pricing.output || item.contextWindow || item.context_window || item.parameters) && (
        <SectionCard title="Quick Specs">
          <div className="divide-y divide-slate-100 -mx-6 -mb-6">
            <InfoRow label="Context Window" value={item.contextWindow || item.context_window} icon={BookOpen} />
            <InfoRow label="Parameters" value={item.parameters} icon={Brain} />
            <InfoRow label="Input pricing" value={pricing.input} icon={CurrencyDollar} />
            <InfoRow label="Output pricing" value={pricing.output} icon={CurrencyDollar} />
            <InfoRow label="Released" value={item.releasedAt || item.release_date} icon={Clock} />
          </div>
        </SectionCard>
      )}
    </>
  );
}

function AgentsContent({ item }) {
  const caps = item.capabilities || [];
  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{stripHtml(item.name)}</h1>
              {(item.isNew || item.is_new) && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">NEW</Badge>}
              {(item.hot || item.isHot) && <Badge className="bg-red-50 text-red-600 border-red-200"><Fire size={9} weight="fill" /> HOT</Badge>}
            </div>
            <p className="text-sm text-slate-500">{stripHtml(item.by || item.creator)} · {item.category}</p>
          </div>
          {item.score && (
            <div className="text-center flex-shrink-0">
              <div className="text-3xl font-black text-blue-600">{item.score}</div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wider">score</div>
            </div>
          )}
        </div>
        <p className="text-slate-700 leading-relaxed mb-5">{stripHtml(item.description)}</p>
        <div className="flex flex-wrap gap-2">
          {item.openSource || item.open_source ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Open Source</Badge> : null}
          {item.pricing && <Badge className="bg-slate-100 text-slate-700 border-slate-200"><CurrencyDollar size={11} /> {item.pricing}</Badge>}
        </div>
        <AttributionFooter item={item} section="agents" />
      </div>

      {caps.length > 0 && (
        <SectionCard title="Capabilities" icon={CheckCircle}>
          <ul className="space-y-2">
            {caps.map((c) => (
              <li key={c} className="flex items-center gap-3">
                <CheckCircle size={15} className="text-blue-600 flex-shrink-0" weight="fill" />
                <span className="text-sm text-slate-700">{c}</span>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}
    </>
  );
}

function ResearchContent({ item }) {
  const authors = item.authors || [];
  const summary = stripHtml(item.summary || item.abstract || "");

  // Split summary into up to 3 contribution points
  const sentences = summary.split(/(?<=[.?!])\s+/).filter(Boolean);
  const keyPoints = sentences.slice(0, 3);

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
        <div className="flex flex-wrap gap-2 mb-4">
          {item.category && <Badge className="bg-amber-50 text-amber-700 border-amber-200">{item.category}</Badge>}
          {item.venue && <Badge className="bg-slate-100 text-slate-600 border-slate-200">{item.venue}</Badge>}
          {(item.isNew || item.is_new) && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">NEW</Badge>}
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 leading-snug mb-4">
          {stripHtml(item.title)}
        </h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-5">
          {item.institution && <span className="font-medium text-slate-700">{stripHtml(item.institution)}</span>}
          {item.publishedAt && <span className="flex items-center gap-1"><Clock size={13} /> {item.publishedAt}</span>}
          {item.citations > 0 && <span className="flex items-center gap-1"><ChartBar size={13} /> {item.citations} citations</span>}
        </div>
        {authors.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-5">
            {authors.slice(0, 6).map((a) => (
              <span key={a} className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-medium">{a}</span>
            ))}
            {authors.length > 6 && <span className="text-xs text-slate-400">+{authors.length - 6} more</span>}
          </div>
        )}
        {summary && <p className="text-slate-700 leading-relaxed text-base mb-4">{summary}</p>}
        {(item.url || item.arxivId || item.arxiv_id) && (
          <a
            href={item.url || `https://arxiv.org/abs/${item.arxivId || item.arxiv_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold text-amber-700 hover:text-amber-800 transition-colors border border-amber-200 bg-amber-50 px-4 py-2 rounded-lg"
          >
            Read on arXiv <ArrowSquareOut size={13} />
          </a>
        )}
        <AttributionFooter item={item} section="research" />
      </div>

      {keyPoints.length > 0 && (
        <SectionCard title="Key Contributions" icon={LightbulbFilament}>
          <InsightsBullets bullets={keyPoints} accentClass="text-amber-600" />
        </SectionCard>
      )}

      {item.tags && item.tags.length > 0 && (
        <SectionCard title="Research Themes" icon={Tag}>
          <div className="flex flex-wrap gap-2">
            {item.tags.map((t) => <TagChip key={t} tag={t} />)}
          </div>
        </SectionCard>
      )}
    </>
  );
}

function CompaniesContent({ item }) {
  const products = item.products || [];
  const investors = item.investors || [];
  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
        <div className="flex items-start gap-5 mb-5">
          <span className="text-5xl">{item.logo || "🤖"}</span>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{stripHtml(item.name)}</h1>
              {(item.hot || item.isHot) && <Badge className="bg-red-50 text-red-600 border-red-200"><Fire size={9} weight="fill" /> HOT</Badge>}
            </div>
            <div className="flex flex-wrap gap-2 text-sm text-slate-500">
              {item.category && <Badge className="bg-slate-100 text-slate-600 border-slate-200">{item.category}</Badge>}
              {item.hq && <span className="flex items-center gap-1"><MapPin size={13} /> {stripHtml(item.hq)}</span>}
              {item.founded && <span>Est. {item.founded}</span>}
            </div>
          </div>
        </div>
        <p className="text-slate-700 leading-relaxed mb-5">{stripHtml(item.description)}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {item.valuation && <div className="bg-slate-50 rounded-xl p-3 text-center"><div className="text-base font-bold text-slate-900">{item.valuation}</div><div className="text-[11px] text-slate-400 mt-0.5">Valuation</div></div>}
          {(item.totalFunding || item.total_funding) && <div className="bg-slate-50 rounded-xl p-3 text-center"><div className="text-base font-bold text-slate-900">{item.totalFunding || item.total_funding}</div><div className="text-[11px] text-slate-400 mt-0.5">Total Funding</div></div>}
          {item.employees && <div className="bg-slate-50 rounded-xl p-3 text-center"><div className="text-base font-bold text-slate-900">{item.employees}</div><div className="text-[11px] text-slate-400 mt-0.5">Employees</div></div>}
          {item.ceo && <div className="bg-slate-50 rounded-xl p-3 text-center"><div className="text-base font-bold text-slate-900 truncate">{stripHtml(item.ceo)}</div><div className="text-[11px] text-slate-400 mt-0.5">CEO</div></div>}
        </div>
        <AttributionFooter item={item} section="companies" />
      </div>

      {item.recentNews && (
        <SectionCard title="Latest Development" icon={Newspaper}>
          <div className="flex items-start gap-3">
            <div className="w-1 rounded-full bg-emerald-400 self-stretch flex-shrink-0" />
            <p className="text-sm text-slate-700 leading-relaxed">{stripHtml(item.recentNews)}</p>
          </div>
        </SectionCard>
      )}

      {products.length > 0 && (
        <SectionCard title="Products & Offerings">
          <div className="flex flex-wrap gap-2">
            {products.map((p) => <TagChip key={p} tag={p} />)}
          </div>
        </SectionCard>
      )}

      {investors.length > 0 && (
        <SectionCard title="Backed By">
          <div className="flex flex-wrap gap-2">
            {investors.map((inv) => <TagChip key={inv} tag={inv} />)}
          </div>
        </SectionCard>
      )}
    </>
  );
}

function StartupsContent({ item }) {
  const investors = item.investors || [];
  const raised = item.funding || item.raised;

  const signalLabel =
    item.round?.includes("Seed") ? "Emerging" :
    item.round?.includes("Series A") || item.round?.includes("Series B") ? "Breakout" :
    "Established";

  const signalColor =
    signalLabel === "Emerging" ? "bg-orange-50 text-orange-700 border-orange-200" :
    signalLabel === "Breakout" ? "bg-blue-50 text-blue-700 border-blue-200" :
    "bg-emerald-50 text-emerald-700 border-emerald-200";

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
        <div className="flex items-start gap-5 mb-5">
          {item.emoji && <span className="text-5xl">{item.emoji}</span>}
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{stripHtml(item.name)}</h1>
              {(item.isNew || item.is_new) && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">NEW</Badge>}
              {(item.hot || item.isHot) && <Badge className="bg-red-50 text-red-600 border-red-200"><Fire size={9} weight="fill" /> HOT</Badge>}
              <Badge className={signalColor}>TrendMing Signal: {signalLabel}</Badge>
            </div>
            <div className="flex flex-wrap gap-3 text-sm text-slate-500">
              {item.round && <Badge className="bg-orange-50 text-orange-700 border-orange-200">{item.round}</Badge>}
              {item.location && <span className="flex items-center gap-1"><MapPin size={13} /> {stripHtml(item.location)}</span>}
              {item.founded && <span>Founded {item.founded}</span>}
            </div>
          </div>
        </div>
        <p className="text-slate-700 leading-relaxed mb-5">{stripHtml(item.description)}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {raised && <div className="bg-emerald-50 rounded-xl p-3 text-center"><div className="text-lg font-bold text-emerald-700">{raised}</div><div className="text-[11px] text-slate-400 mt-0.5">Raised</div></div>}
          {item.valuation && <div className="bg-slate-50 rounded-xl p-3 text-center"><div className="text-base font-bold text-slate-900">{item.valuation}</div><div className="text-[11px] text-slate-400 mt-0.5">Valuation</div></div>}
          {item.category && <div className="bg-slate-50 rounded-xl p-3 text-center"><div className="text-sm font-bold text-slate-900">{item.category}</div><div className="text-[11px] text-slate-400 mt-0.5">Category</div></div>}
        </div>
        <AttributionFooter item={item} section="startups" />
      </div>

      {investors.length > 0 && (
        <SectionCard title="Investor Signals">
          <p className="text-xs text-slate-500 mb-3">Lead investors backing this venture:</p>
          <div className="flex flex-wrap gap-2">
            {investors.map((inv) => (
              <span key={inv} className="text-xs font-semibold px-3 py-1 bg-green-50 text-green-700 border border-green-200 rounded-full">{inv}</span>
            ))}
          </div>
        </SectionCard>
      )}
    </>
  );
}

function FundingContent({ item }) {
  const investors = item.investors || item.leadInvestors || item.lead_investors || [];
  const categorySignals = {
    "Foundation Models": "signals investor conviction in the core infrastructure layer of AI",
    "AI Coding": "reflects strong demand for developer productivity tools powered by AI",
    "AI Infrastructure": "shows the ongoing buildout of compute and tooling required to run AI at scale",
    "AI Chips": "indicates hardware competition intensifying as AI workloads grow exponentially",
    "AI Video": "points to multimodal AI extending beyond text into rich media creation",
  };
  const signal = categorySignals[item.category] || "reflects continued capital concentration in the AI sector";

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
        <div className="flex flex-wrap gap-2 mb-4">
          {item.round && <Badge className="bg-green-50 text-green-700 border-green-200">{item.round}</Badge>}
          {item.category && <Badge className="bg-slate-100 text-slate-600 border-slate-200">{item.category}</Badge>}
          {(item.isNew || item.is_new) && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">NEW</Badge>}
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">{stripHtml(item.company)}</h1>
        {item.amount && (
          <div className="text-4xl font-black text-emerald-600 mb-4">{item.amount}</div>
        )}
        <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-4">
          {item.date && <span className="flex items-center gap-1"><Clock size={13} /> {item.date}</span>}
          {item.valuation && <span>Valuation: <strong className="text-slate-700">{item.valuation}</strong></span>}
        </div>
        {item.description && <p className="text-slate-700 leading-relaxed mb-4">{stripHtml(item.description)}</p>}
        <AttributionFooter item={item} section="funding" />
      </div>

      <SectionCard title="What This Round Signals" icon={LightbulbFilament}>
        <p className="text-sm text-slate-700 leading-relaxed">
          This {item.round || "round"} for <strong>{stripHtml(item.company)}</strong> {signal}.{" "}
          {investors.length > 0 && `Participation from ${investors.slice(0, 2).join(" and ")} underscores institutional confidence in this category.`}
        </p>
      </SectionCard>

      {investors.length > 0 && (
        <SectionCard title="Lead Investors">
          <div className="flex flex-wrap gap-2">
            {investors.map((inv) => (
              <span key={inv} className="text-xs font-semibold px-3 py-1 bg-green-50 text-green-700 border border-green-200 rounded-full">{inv}</span>
            ))}
          </div>
        </SectionCard>
      )}
    </>
  );
}

function EventsContent({ item }) {
  const speakers = item.speakers || [];
  const tags = item.tags || [];
  const isFree = item.free || item.isFree || item.is_free;
  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
        <div className="flex flex-wrap gap-2 mb-4">
          {item.type && <Badge className="bg-pink-50 text-pink-700 border-pink-200">{item.type}</Badge>}
          {item.format && <Badge className="bg-slate-100 text-slate-600 border-slate-200">{item.format}</Badge>}
          {isFree ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Free</Badge> : <Badge className="bg-amber-50 text-amber-700 border-amber-200">Paid</Badge>}
          {(item.isNew || item.is_new) && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">NEW</Badge>}
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">{stripHtml(item.name)}</h1>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
          {item.date && <div className="bg-pink-50 rounded-xl p-3 text-center"><div className="text-sm font-bold text-pink-700">{item.date}</div><div className="text-[11px] text-slate-400 mt-0.5">Date</div></div>}
          {item.location && <div className="bg-slate-50 rounded-xl p-3 text-center"><div className="text-sm font-bold text-slate-900">{stripHtml(item.location)}</div><div className="text-[11px] text-slate-400 mt-0.5">Location</div></div>}
          {item.attendees && <div className="bg-slate-50 rounded-xl p-3 text-center"><div className="text-sm font-bold text-slate-900">{typeof item.attendees === "number" ? item.attendees.toLocaleString() : item.attendees}</div><div className="text-[11px] text-slate-400 mt-0.5">Expected Attendees</div></div>}
        </div>
        <p className="text-slate-700 leading-relaxed mb-5">{stripHtml(item.description)}</p>
        {(item.website || item.url) && (
          <a
            href={item.website || item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold text-pink-700 hover:text-pink-800 transition-colors border border-pink-200 bg-pink-50 px-4 py-2 rounded-lg"
          >
            Register / Learn More <ArrowSquareOut size={13} />
          </a>
        )}
        <AttributionFooter item={item} section="events" />
      </div>

      {speakers.length > 0 && (
        <SectionCard title="Featured Speakers">
          <div className="flex flex-wrap gap-2">
            {speakers.map((s) => (
              <span key={s} className="text-xs font-medium px-3 py-1 bg-pink-50 text-pink-700 border border-pink-200 rounded-full">{s}</span>
            ))}
          </div>
        </SectionCard>
      )}

      {tags.length > 0 && (
        <SectionCard title="Topics" icon={Tag}>
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => <TagChip key={t} tag={t} />)}
          </div>
        </SectionCard>
      )}
    </>
  );
}

function LearningContent({ item }) {
  const topics = item.topics || item.tags || [];
  const level = item.level || "";

  const audienceGuidance = {
    Beginner: "Great starting point if you're new to AI or machine learning. No prior experience required.",
    Intermediate: "Ideal for practitioners who understand the basics and want to build production-ready skills.",
    Advanced: "Designed for researchers and engineers looking to push beyond standard applications.",
  };
  const guidance = audienceGuidance[level] || null;

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
        <div className="flex flex-wrap gap-2 mb-4">
          {item.type && <Badge className="bg-yellow-50 text-yellow-700 border-yellow-200">{item.type}</Badge>}
          {level && <Badge className="bg-blue-50 text-blue-700 border-blue-200"><Certificate size={11} /> {level}</Badge>}
          {(item.isNew || item.is_new) && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">NEW</Badge>}
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">{stripHtml(item.title || item.name)}</h1>
        <p className="text-sm text-slate-500 mb-5">by {stripHtml(item.provider)}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {item.price && <div className="bg-slate-50 rounded-xl p-3 text-center"><div className="text-base font-bold text-slate-900">{item.price}</div><div className="text-[11px] text-slate-400 mt-0.5">Price</div></div>}
          {item.duration && <div className="bg-slate-50 rounded-xl p-3 text-center"><div className="text-base font-bold text-slate-900">{item.duration}</div><div className="text-[11px] text-slate-400 mt-0.5">Duration</div></div>}
          {item.rating && <div className="bg-amber-50 rounded-xl p-3 text-center"><div className="text-base font-bold text-amber-700 flex items-center justify-center gap-1"><Star size={14} weight="fill" />{item.rating}</div><div className="text-[11px] text-slate-400 mt-0.5">Rating</div></div>}
          {item.students && <div className="bg-slate-50 rounded-xl p-3 text-center"><div className="text-base font-bold text-slate-900">{typeof item.students === "number" ? item.students.toLocaleString() : item.students}</div><div className="text-[11px] text-slate-400 mt-0.5">Students</div></div>}
        </div>
        <p className="text-slate-700 leading-relaxed mb-4">{stripHtml(item.description)}</p>
        {(item.url || item.website) && (
          <a
            href={item.url || item.website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold text-yellow-700 hover:text-yellow-800 transition-colors border border-yellow-200 bg-yellow-50 px-4 py-2 rounded-lg"
          >
            Enroll Now <ArrowSquareOut size={13} />
          </a>
        )}
        <AttributionFooter item={item} section="learning" />
      </div>

      {guidance && (
        <SectionCard title="Is This Right For You?" icon={LightbulbFilament}>
          <div className="flex items-start gap-3">
            <Certificate size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" weight="fill" />
            <p className="text-sm text-slate-700 leading-relaxed">{guidance}</p>
          </div>
        </SectionCard>
      )}

      {topics.length > 0 && (
        <SectionCard title="Curriculum Highlights">
          <ol className="space-y-2">
            {topics.map((t, i) => (
              <li key={t} className="flex items-center gap-3">
                <span className="text-xs font-bold text-yellow-600 w-5 text-right">{i + 1}.</span>
                <span className="text-sm text-slate-700">{t}</span>
              </li>
            ))}
          </ol>
        </SectionCard>
      )}
    </>
  );
}

function CodingContent({ item }) {
  const tags = item.tags || [];
  const features = item.features || item.highlights || [];
  const bestFor = tags.length > 0 ? tags.slice(0, 3).join(", ") : item.category || null;
  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
        <div className="flex flex-wrap gap-2 mb-4">
          {item.category && <Badge className="bg-cyan-50 text-cyan-700 border-cyan-200">{item.category}</Badge>}
          {(item.pricing || item.price) && <Badge className="bg-slate-100 text-slate-600 border-slate-200"><CurrencyDollar size={11} /> {item.pricing || item.price}</Badge>}
          {(item.isNew || item.is_new) && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">NEW</Badge>}
          {(item.hot || item.isHot) && <Badge className="bg-red-50 text-red-600 border-red-200"><Fire size={9} weight="fill" /> HOT</Badge>}
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1">{stripHtml(item.name)}</h1>
        {item.maker && <p className="text-sm text-slate-500 mb-5">by {stripHtml(item.maker)}</p>}
        <p className="text-slate-700 leading-relaxed">{stripHtml(item.description)}</p>
        {bestFor && (
          <p className="text-sm text-slate-500 mt-4"><strong>Best for:</strong> {bestFor}</p>
        )}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {tags.map((t) => <TagChip key={t} tag={t} />)}
          </div>
        )}
        <AttributionFooter item={item} section="coding" />
      </div>

      {features.length > 0 && (
        <SectionCard title="Key Features" icon={CheckCircle}>
          <ul className="space-y-2">
            {features.map((f) => (
              <li key={f} className="flex items-center gap-3">
                <CheckCircle size={15} className="text-cyan-600 flex-shrink-0" weight="fill" />
                <span className="text-sm text-slate-700">{f}</span>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}
    </>
  );
}

function ToolsContent({ item }) {
  const highlights = item.highlights || [];
  const tags = item.tags || [];
  const score = item.score || 0;
  const tier = score >= 85 ? "Elite" : score >= 70 ? "Strong" : "Good";
  const bestFor = tags.length > 0 ? tags.slice(0, 3).join(", ") : item.category;
  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
        <div className="flex items-start gap-5 mb-5">
          {item.emoji && <span className="text-5xl">{item.emoji}</span>}
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{stripHtml(item.name)}</h1>
              {(item.isNew || item.is_new) && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">NEW</Badge>}
              {(item.hot || item.isHot) && <Badge className="bg-red-50 text-red-600 border-red-200"><Fire size={9} weight="fill" /> HOT</Badge>}
            </div>
            <p className="text-sm text-slate-500">{stripHtml(item.by || item.maker)} · {item.category}</p>
          </div>
          {score > 0 && (
            <div className="text-center flex-shrink-0">
              <div className="text-3xl font-black text-purple-600">{score}</div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wider">{tier}</div>
            </div>
          )}
        </div>
        <p className="text-slate-700 leading-relaxed mb-4">{stripHtml(item.description)}</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {item.pricing && <Badge className="bg-slate-100 text-slate-600 border-slate-200"><CurrencyDollar size={11} /> {item.pricing}</Badge>}
          {item.founded && <Badge className="bg-slate-100 text-slate-600 border-slate-200">Founded {item.founded}</Badge>}
        </div>
        {score > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-slate-500">TrendMing Trend Score</span>
              <span className="text-xs font-bold text-purple-600">{score} / 100</span>
            </div>
            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${score}%` }} />
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">Score reflects adoption, capability, and community momentum as assessed by TrendMing.</p>
          </div>
        )}
        <AttributionFooter item={item} section="tools" />
      </div>

      {highlights.length > 0 && (
        <SectionCard title="Highlights" icon={CheckCircle}>
          <ul className="space-y-2">
            {highlights.map((h) => (
              <li key={h} className="flex items-center gap-3">
                <CheckCircle size={15} className="text-purple-600 flex-shrink-0" weight="fill" />
                <span className="text-sm text-slate-700">{h}</span>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      {bestFor && (
        <SectionCard title="Best For">
          <p className="text-sm text-slate-700">{bestFor}</p>
        </SectionCard>
      )}

      {tags.length > 0 && (
        <SectionCard title="Tags" icon={Tag}>
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => <TagChip key={t} tag={t} />)}
          </div>
        </SectionCard>
      )}
    </>
  );
}

function MarketplaceContent({ item }) {
  const tags = item.tags || [];
  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
        <div className="flex flex-wrap gap-2 mb-4">
          {item.category && <Badge className="bg-teal-50 text-teal-700 border-teal-200">{item.category}</Badge>}
          {item.type && <Badge className="bg-slate-100 text-slate-600 border-slate-200">{item.type}</Badge>}
          {(item.isNew || item.is_new) && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">NEW</Badge>}
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1">{stripHtml(item.name)}</h1>
        <p className="text-sm text-slate-500 mb-3">by {stripHtml(item.provider)}</p>
        <div className="flex items-center gap-3 mb-5">
          {item.rating && (
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={14} weight={i < Math.round(item.rating) ? "fill" : "regular"} className={i < Math.round(item.rating) ? "text-yellow-400" : "text-slate-200"} />
              ))}
              <span className="text-sm font-bold text-slate-700 ml-1">{item.rating}</span>
            </div>
          )}
          {item.reviews > 0 && <span className="text-sm text-slate-400">({item.reviews.toLocaleString()} reviews)</span>}
          {(item.price || item.pricing) && <span className="text-sm font-bold text-teal-700 ml-2">{item.price || item.pricing}</span>}
        </div>
        <p className="text-slate-700 leading-relaxed">{stripHtml(item.description)}</p>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-5">
            {tags.map((t) => <TagChip key={t} tag={t} />)}
          </div>
        )}
        <AttributionFooter item={item} section="marketplace" />
      </div>
    </>
  );
}

function AIRecommendedCard({ item, section }) {
  const [imgFailed, setImgFailed] = useState(false);
  const thumb = item.thumbnail;
  const slug = item.slug || item.id;
  const to = `/trending-ai/${section}/${slug}`;
  return (
    <Link
      to={to}
      className="group block bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md rounded-xl overflow-hidden transition-all"
    >
      <div className="relative aspect-[16/9] bg-slate-100 overflow-hidden">
        {!imgFailed && thumb ? (
          <img
            src={thumb}
            alt={stripHtml(item.title || item.name || "")}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-slate-800 to-blue-950 flex items-center justify-center">
            <Newspaper size={28} weight="duotone" className="text-blue-400 opacity-60" />
          </div>
        )}
        {item.category && (
          <div className="absolute top-3 left-3">
            <span className="text-[10px] font-medium px-2 py-0.5 bg-white/90 backdrop-blur text-slate-700 border border-slate-200 rounded capitalize">
              {item.category}
            </span>
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors leading-snug line-clamp-2">
          {stripHtml(item.title || item.name || "")}
        </h3>
        {(item.publishedAt || item.published_at || item.date) && (
          <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1">
            <Clock size={10} />
            {item.publishedAt || item.published_at || item.date}
          </p>
        )}
      </div>
    </Link>
  );
}

function SectionContent({ section, item }) {
  switch (section) {
    case "news":        return <NewsContent item={item} />;
    case "models":      return <ModelsContent item={item} />;
    case "agents":      return <AgentsContent item={item} />;
    case "research":    return <ResearchContent item={item} />;
    case "companies":   return <CompaniesContent item={item} />;
    case "startups":    return <StartupsContent item={item} />;
    case "funding":     return <FundingContent item={item} />;
    case "events":      return <EventsContent item={item} />;
    case "learning":    return <LearningContent item={item} />;
    case "coding":      return <CodingContent item={item} />;
    case "tools":       return <ToolsContent item={item} />;
    case "marketplace": return <MarketplaceContent item={item} />;
    default:            return <p className="text-slate-500">No detail view for this section.</p>;
  }
}

// ── External CTA helpers ──────────────────────────────────────────────────────

function getExternalLink(section, item) {
  switch (section) {
    case "news":        return { href: item.url, label: "Read Full Article" };
    case "research":    return { href: item.url || (item.arxivId || item.arxiv_id ? `https://arxiv.org/abs/${item.arxivId || item.arxiv_id}` : null), label: "View on arXiv" };
    case "events":      return { href: item.website || item.url, label: "Register Now" };
    case "learning":    return { href: item.website || item.url, label: "Enroll Now" };
    case "companies":
    case "startups":
    case "agents":
    case "coding":
    case "tools":
    case "marketplace": return { href: item.website || item.url, label: "Visit Website" };
    default:            return null;
  }
}

function itemName(section, item) {
  if (section === "news" || section === "research" || section === "learning") return item.title || item.name;
  if (section === "funding") return item.company;
  return item.name;
}

function itemEmoji(section, item) {
  if (item.emoji) return item.emoji;
  if (item.logo) return item.logo;
  const defaults = {
    news: "📰", models: "🧠", agents: "🤖", research: "📄", companies: "🏢",
    startups: "🚀", funding: "💰", events: "📅", learning: "🎓", coding: "💻",
    tools: "🔧", marketplace: "🛍️",
  };
  return defaults[section] || "✨";
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AIItemDetailPage() {
  const { section, slug } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const meta = SECTION_META[section] || { label: "AI Intelligence", Icon: Brain, color: "bg-slate-100 text-slate-600 border-slate-200", accent: "text-blue-600", refreshLabel: "daily" };
  const exploreLinks = EXPLORE_LINKS.filter((l) => l.section !== section).slice(0, 6);

  useEffect(() => {
    if (!section || !slug) return;
    setLoading(true);
    setNotFound(false);
    setItem(null);
    setRelated([]);

    let fetchedItem = null;
    fetchAIItem(section, slug)
      .then((data) => {
        fetchedItem = data;
        setItem(data);
        return fetchAISection(section, { page: 1, page_size: 9 });
      })
      .then((list) => {
        if (!fetchedItem) return;
        const items = Array.isArray(list) ? list : (list?.items ?? []);
        const others = items.filter((i) => i.id !== fetchedItem.id && i.slug !== fetchedItem.slug);
        setRelated(others.slice(0, 6));
      })
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [section, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const externalLink = item ? getExternalLink(section, item) : null;
  const pageTitle = item ? (itemName(section, item) || "") : "";
  const seoTitle = pageTitle ? `${pageTitle} — ${meta.label} · TrendMing` : `${meta.label} · TrendMing`;
  const seoDesc = item ? stripHtml(item.description || item.excerpt || item.summary || item.abstract || "") : "";

  if (loading) return (
    <>
      <AINavBar />
      <PageSkeleton />
    </>
  );

  if (notFound) return (
    <>
      <AINavBar />
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-20 text-center">
        <p className="text-5xl mb-4">🔍</p>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Item not found</h1>
        <p className="text-slate-500 mb-6">This item may have been removed or the link is incorrect.</p>
        <Link to={`/trending-ai/${section}`} className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-blue-700 transition-colors">
          <ArrowLeft size={14} /> Back to {meta.label}
        </Link>
      </div>
    </>
  );

  if (!item) return null;

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDesc.slice(0, 160)}
        canonical={`https://trendming.com/trending-ai/${section}/${slug}`}
      />
      <AINavBar />

      {/* Breadcrumb */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-3">
          <nav aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-xs text-slate-400 flex-wrap">
              <li><Link to="/" className="hover:text-slate-700 transition-colors">Home</Link></li>
              <span>/</span>
              <li><Link to="/trending-ai" className="hover:text-slate-700 transition-colors">AI Intelligence</Link></li>
              <span>/</span>
              <li><Link to={`/trending-ai/${section}`} className="hover:text-slate-700 transition-colors">{meta.label}</Link></li>
              <span>/</span>
              <li className="text-slate-700 font-medium truncate max-w-[200px]">{pageTitle}</li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* ── Main content ── */}
          <main className="lg:col-span-2 space-y-5">
            <SectionContent section={section} item={item} />

            {/* Recommended for You — shown for news items */}
            {section === "news" && related.length > 0 && (
              <div className="pt-4 border-t border-slate-200">
                <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">/ Recommended</span>
                    <h2 className="font-bold text-xl text-slate-900 mt-1">Recommended for You</h2>
                  </div>
                  <Link
                    to="/trending-ai/news"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-blue-600 transition-colors"
                  >
                    All AI news <ArrowRight size={12} />
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {related.map((rel) => (
                    <AIRecommendedCard key={rel.id || rel.slug} item={rel} section={section} />
                  ))}
                </div>
              </div>
            )}

            <div className="pt-2">
              <Link
                to={`/trending-ai/${section}`}
                className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-blue-600 transition-colors"
              >
                <ArrowLeft size={14} /> Back to {meta.label}
              </Link>
            </div>
          </main>

          {/* ── Sidebar ── */}
          <aside className="space-y-5">

            {/* TrendMing Intelligence card */}
            <TrendMingIntelligenceCard section={section} meta={meta} />

            {/* Meta card */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                <meta.Icon size={13} className={meta.accent} />
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{meta.label}</h3>
              </div>
              <div className="divide-y divide-slate-100">
                {section === "news" && <>
                  <InfoRow label="Author" value={item.author} />
                  <InfoRow label="Read time" value={item.readTime} icon={Clock} />
                  <InfoRow label="Published" value={item.publishedAt || item.published_at || item.date} icon={Clock} />
                  <InfoRow label="Views" value={item.views > 0 ? item.views.toLocaleString() : null} />
                </>}
                {section === "models" && <>
                  <InfoRow label="Provider" value={item.provider} />
                  <InfoRow label="Family" value={item.family || item.model_family} />
                  <InfoRow label="Context Window" value={item.contextWindow || item.context_window} />
                  <InfoRow label="Released" value={item.releasedAt || item.release_date} icon={Clock} />
                  <InfoRow label="Rank" value={item.rank ? `#${item.rank}` : null} />
                </>}
                {section === "agents" && <>
                  <InfoRow label="Creator" value={item.by || item.creator} />
                  <InfoRow label="Category" value={item.category} />
                  <InfoRow label="Pricing" value={item.pricing} icon={CurrencyDollar} />
                  <InfoRow label="Score" value={item.score} />
                </>}
                {section === "research" && <>
                  <InfoRow label="Venue" value={item.venue} />
                  <InfoRow label="Institution" value={item.institution} />
                  <InfoRow label="Published" value={item.publishedAt} icon={Clock} />
                  <InfoRow label="Citations" value={item.citations > 0 ? item.citations : null} icon={ChartBar} />
                </>}
                {section === "companies" && <>
                  <InfoRow label="HQ" value={item.hq} icon={MapPin} />
                  <InfoRow label="Founded" value={item.founded} icon={Clock} />
                  <InfoRow label="CEO" value={item.ceo} />
                  <InfoRow label="Stage" value={item.stage} />
                  <InfoRow label="Employees" value={item.employees} icon={Users} />
                </>}
                {section === "startups" && <>
                  <InfoRow label="Location" value={item.location} icon={MapPin} />
                  <InfoRow label="Founded" value={item.founded} icon={Clock} />
                  <InfoRow label="Round" value={item.round} />
                  <InfoRow label="Category" value={item.category} />
                  <InfoRow label="Website" value={item.website} icon={Globe} />
                </>}
                {section === "funding" && <>
                  <InfoRow label="Round" value={item.round} />
                  <InfoRow label="Amount" value={item.amount} icon={CurrencyDollar} />
                  <InfoRow label="Date" value={item.date} icon={Clock} />
                  <InfoRow label="Valuation" value={item.valuation} />
                  <InfoRow label="Category" value={item.category} />
                </>}
                {section === "events" && <>
                  <InfoRow label="Type" value={item.type} />
                  <InfoRow label="Date" value={item.date} icon={Clock} />
                  <InfoRow label="End Date" value={item.endDate || item.end_date} />
                  <InfoRow label="Location" value={item.location} icon={MapPin} />
                  <InfoRow label="Format" value={item.format} />
                </>}
                {section === "learning" && <>
                  <InfoRow label="Provider" value={item.provider} />
                  <InfoRow label="Type" value={item.type} />
                  <InfoRow label="Level" value={item.level} icon={Certificate} />
                  <InfoRow label="Duration" value={item.duration} icon={Clock} />
                  <InfoRow label="Price" value={item.price} icon={CurrencyDollar} />
                </>}
                {section === "coding" && <>
                  <InfoRow label="Maker" value={item.maker} />
                  <InfoRow label="Category" value={item.category} />
                  <InfoRow label="Pricing" value={item.pricing || item.price} icon={CurrencyDollar} />
                  <InfoRow label="Website" value={item.website} icon={Globe} />
                </>}
                {section === "tools" && <>
                  <InfoRow label="By" value={item.by || item.maker} />
                  <InfoRow label="Category" value={item.category} />
                  <InfoRow label="Pricing" value={item.pricing} icon={CurrencyDollar} />
                  <InfoRow label="Founded" value={item.founded} icon={Clock} />
                  <InfoRow label="Score" value={item.score ? `${item.score} / 100` : null} />
                </>}
                {section === "marketplace" && <>
                  <InfoRow label="Provider" value={item.provider} />
                  <InfoRow label="Category" value={item.category} />
                  <InfoRow label="Type" value={item.type} />
                  <InfoRow label="Price" value={item.price || item.pricing} icon={CurrencyDollar} />
                  <InfoRow label="Rating" value={item.rating ? `${item.rating} / 5` : null} icon={Star} />
                </>}
              </div>
            </div>

            {/* External CTA */}
            {externalLink?.href && (
              <a
                href={externalLink.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-5 py-3 rounded-xl transition-colors"
              >
                {externalLink.label} <ArrowUpRight size={14} />
              </a>
            )}

            {/* Related items */}
            {related.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Related</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {related.slice(0, 3).map((rel) => {
                    const relSlug = rel.slug || rel.id;
                    const relName = itemName(section, rel);
                    const relDesc = stripHtml(rel.description || rel.excerpt || rel.summary || rel.abstract || "");
                    return (
                      <Link
                        key={rel.id || rel.slug}
                        to={`/trending-ai/${section}/${relSlug}`}
                        className="flex items-center gap-3 px-5 py-3.5 hover:bg-slate-50 transition-colors group"
                      >
                        <span className="text-xl flex-shrink-0">{itemEmoji(section, rel)}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors leading-snug truncate">{relName}</p>
                          {relDesc && <p className="text-[11px] text-slate-400 truncate mt-0.5">{relDesc}</p>}
                        </div>
                        <ArrowRight size={12} className="text-slate-300 group-hover:text-blue-400 flex-shrink-0 transition-colors" />
                      </Link>
                    );
                  })}
                </div>
                <div className="px-5 py-3 border-t border-slate-100">
                  <Link
                    to={`/trending-ai/${section}`}
                    className="text-xs font-semibold text-blue-600 hover:underline inline-flex items-center gap-1"
                  >
                    All {meta.label} <ArrowRight size={11} />
                  </Link>
                </div>
              </div>
            )}

            {/* Explore more */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Explore AI Intelligence</h3>
              </div>
              <div className="divide-y divide-slate-100">
                {exploreLinks.map(({ section: sec, label, Icon: SIcon }) => (
                  <Link
                    key={sec}
                    to={`/trending-ai/${sec}`}
                    className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors group"
                  >
                    <SIcon size={14} className="text-slate-400 group-hover:text-blue-600 transition-colors flex-shrink-0" />
                    <span className="text-sm text-slate-600 group-hover:text-blue-600 transition-colors">{label}</span>
                    <ArrowRight size={11} className="ml-auto text-slate-300 group-hover:text-blue-400 transition-colors" />
                  </Link>
                ))}
              </div>
              <div className="px-5 py-3 border-t border-slate-100">
                <Link to="/trending-ai" className="text-xs font-semibold text-blue-600 hover:underline inline-flex items-center gap-1">
                  AI Intelligence Hub <ArrowRight size={11} />
                </Link>
              </div>
            </div>

          </aside>
        </div>
      </div>
    </>
  );
}
